import './SummaryTable.css';
import { Property } from '../../../../models/Property';
import { 
	additionalAmenitiesFormatter, 
	displayBuilding, 
	incentiveFormatter, 
	propertyHighwayFormatter, 
	specialtyFeaturesFormatter, 
	toFormattedString, 
	utilityStringBuilder, 
	zoningFormatter 
} from '../../../../utilities/propertyUtilities';
import Card from '../../../shared/card/Card';
import { useCallback, useMemo } from 'react';

interface SummaryTableProps {
	property: Property;
}

export default function SummaryTable({ property }: SummaryTableProps) {
	const { site } = property;

	const geo = property.geometry as __esri.Polygon;
	const centroid: __esri.Point = geo.centroid;
	const coords: Record<string, string | null> = {
		latitude: centroid.latitude.toFixed(6),
		longitude: centroid.longitude.toFixed(6)
	};

	const coordString = coords.latitude && coords.longitude ? `${coords.latitude} | ${coords.longitude}` : 'N/A';
	// const siteTransload = site?.specialtyFeatures?.includes('transload');
	// const buildingTransload = building?.specialtyFeatures?.includes('transload');
	// const transload = (siteTransload || buildingTransload) ? 'Yes' : 'No';

	let floodPlain: string;
	if (property.inFloodPlain?.toLowerCase() === 'no') {
		floodPlain = 'No';
	} else if (property.inFloodPlain?.toLowerCase() === 'yes') {
		floodPlain = `Yes - ${property.floodPlainDesignation}`;
	} else {
		floodPlain = 'Unknown';
	}

	//Utilities
	const internet = utilityStringBuilder(site?.cabelInternetProvider, site?.cabelInternet);
	const electric = utilityStringBuilder(site?.electricalProvider, site?.electricalCapcity);
	const gas = utilityStringBuilder(site?.gasProvider, site?.gasCapacity);
	const stormWater = utilityStringBuilder(site?.stormWaterProvider, site?.stormWaterCapacity);
	const wasteWater = utilityStringBuilder(site?.sewerProvider, site?.sewerCapacity);
	const water = utilityStringBuilder(site?.waterProvider, site?.waterCapacity);

	const amenities = additionalAmenitiesFormatter(property.additionalAmenities, property.additionalAmenitiesOther);

	// CSS className, updates grid based 
	// on if site, building or both are visible
	let summaryTable = 'summary-table';
	if (!site && displayBuilding(property)) {
		summaryTable += '__building';
	} else if (site && !displayBuilding(property)) {
		summaryTable += '__site';
	}

	const buildingProfileRenderItems = useMemo(() => {
		const items: RenderItem[] = [
			{ label: 'Tenancy', value: property.building?.tenancy ?? 'N/A' },
			{ label: 'LTB Ratio', value: property.building?.ltbRatio ?? '0' },
			{ label: 'Building SqFt', value: toFormattedString(property.building?.areaSqFt ?? 0) ?? '0' },
			{ label: 'Building Type', value: property.building?.buildingType ?? 'N/A', className: 'capitalize' },
			{ label: 'Warehouse SqFt', value: toFormattedString(property.building?.warehouseSqFt ?? 0) ?? '0' },
			{ label: 'Office SqFt', value: toFormattedString(property.building?.officeSqFt ?? 0) ?? '0' },
			{ label: 'Number of Dock Doors', value: property.building?.numberOfDockDoors ?? '0' },
			{ label: 'Number of Drive-in Doors', value: property.building?.numberOfDriveInDoors ?? '0' },
			{ label: 'Rail Spots', value: property?.numberOfCarSpots ?? '0' }
		];

		return items;

	}, [property]);

	const siteProfileRenderItems = useMemo(() => {
		if (!property.site) return [];
		const items: RenderItem[] = [
			{ label: 'Acreage', value: property.site?.areaAcres ?? '0' },
			{ label: 'Within City Limits', value: property.site?.withinCity ?? 'N/A' },
			{ label: 'County', value: property.site?.county ?? 'N/A' },
			{ label: 'Latitude/Longitude', value: coordString ?? 'N/A' },
			{ label: 'Rail Access', value: property.site?.siteRailServed ?? 'N/A' },
			{ label: 'Outdoor Storage', value: property.building?.outdoorStorage ?? 'N/A' },
			{ label: 'Transloading Available', value: property.transloading },
			{ label: 'Specialty Features', value: specialtyFeaturesFormatter(property.site) ?? 'N/A' },
			{ label: 'Incentives', value: incentiveFormatter(property.incentives) ?? 'N/A' },
			{ label: 'Flood Zone', value: floodPlain },
			// {label: 'Foreign Trade Zone', value: foreignTradeZone},
			{ label: 'Zoning', value: zoningFormatter(property.site) ?? 'N/A' },
			{ label: 'Additional Amenities', value: amenities ?? 'N/A' },
			...!property.building ?
				[{ label: 'Rail Spots', value: property.numberOfCarSpots ?? '0' }]
				: []
			,
		];

		return items;

	}, [property, coordString, floodPlain, amenities]);

	const utilitiesRenderItems = useMemo(() => {
		const items: RenderItem[] = [
			{ label: 'Cable/Internet', value: internet ?? 'N/A' },
			{ label: 'Electric', value: electric ?? 'N/A' },
			{ label: 'Natural Gas', value: gas ?? 'N/A' },
			{ label: 'Stormwater', value: stormWater ?? 'N/A' },
			{ label: 'Wastewater', value: wasteWater ?? 'N/A' },
			{ label: 'Water', value: water ?? 'N/A' }
		];

		return items;

	}, [internet, electric, gas, stormWater, wasteWater, water]);

	const transportationRenderItems = useMemo(() => {
		const items: RenderItem[] = [
			{label: 'Nearest Highway', value: propertyHighwayFormatter(property) ?? 'N/A'},
			{label: 'Nearest Interstate', value: property.nearestInterstate ?? 'N/A'},
			{label: 'Nearest Airport', value: property.nearestAirport ?? 'N/A'},
			{label: 'Nearest Port', value: property.nearestPortName ?? 'N/A'},
			{label: 'Rail-Served', value: property.site?.siteRailServed ?? property.building?.buildingRailServed ?? 'N/A'},
			{label: 'Railroad', value: property.site?.siteRailProvided ?? property.building?.buildingRailProvider ?? 'N/A'}
		];

		return items;

	}, [property]);

	return (
		<div className={summaryTable}>
			{displayBuilding(property) && property.building &&
				<TableGroup 
					items={buildingProfileRenderItems} 
					title='Building Profile' 
					gridSection='building-profile'
				/>
			}

			{property.site &&
				<TableGroup
					items={siteProfileRenderItems}
					title='Site Profile'
					gridSection='site-profile'
				/>
			}

			<TableGroup
				items={utilitiesRenderItems}
				title='Utilities'
				gridSection='utilities'
			/>

			<TableGroup
				items={transportationRenderItems}
				title='Transportation'
				gridSection='transportation'
			/>
		</div>
	);
}

interface TableItemProps {
	label: string;
	value: string | number;
	className?: string;
}
export function TableItem({ label, value, className }: TableItemProps) {
	return (
		<div className={`table-item ${className}`}>
			<div className='table-item-label'>{label}</div>
			<div className='table-item-value'>{value}</div>
		</div>
	);
}

interface RenderItem {
	label: string;
	value: string | number;
	className?: string;
}

interface TableGroupProps {
	items: RenderItem[];
	title: string;
	gridSection: string;
}

function TableGroup({ items, title, gridSection }: TableGroupProps) {

	const renderItems = useCallback((items: RenderItem[]) => {
		return items.map((item) => (
			<TableItem
				key={`summary-table-item--${item.label}`}
				label={item.label}
				value={item.value}
				className={item.className ?? ''}
			/>
		));
	}, []);

	return (
		<Card className={gridSection}>
			<div className="item-wrapper">
				<div>{title}</div>
				<div className="item-container">
					{renderItems(items)}
				</div>
			</div>
		</Card>
	);
}
